import React, { useMemo } from 'react';
import Button from 'jbc-front/components/Button';
import { useMutation, useQuery, LoadingPage } from '../components';
import { gql } from '@apollo/client';
import { reduxForm } from 'redux-form';
import { Panel, Headding, Item, Lead, Buttons, Text, Line } from '../components/PrimaryPanel';
import ToggleButton from '../components/ToggleButton';
import useReactRouter from 'use-react-router';
import _ from 'lodash';
import { CustomDescription } from '../components/QuestionDescription';
import styles from './HasInsurances.scss';

const options = [
  { value: true, label: 'はい' },
  { value: false, label: 'いいえ' }
];

const INSURANCE_STATUSES_FRAGMENT = gql`
  fragment InsuranceStatusesFragment on Profile {
    generalLifeInsurances {
      id
    }
    nursingMedicalInsurances {
      id
    }
    individualAnnuityInsurances {
      id
    }
    earthquakeInsurances {
      id
    }
    socialInsurances {
      id
    }
    premium {
      id
    }
    housingLoan {
      id
    }
  }
`;

const ANSWER_HAS_INSURANCES = gql`
  mutation answerHasInsurances($input: AnswerHasInsurancesInput!) {
    answerHasInsurances(input: $input) {
      questions {
        id
        status
      }
      profile {
        id
        ...InsuranceStatusesFragment
        dependents {
          id
        }
      }
    }
  }
  ${INSURANCE_STATUSES_FRAGMENT}
`;

const INSURANCE_STATUSES = gql`
  query insuranceStatuses {
    request {
      id
      profile {
        id
        ...InsuranceStatusesFragment
      }
    }
  }
  ${INSURANCE_STATUSES_FRAGMENT}
`;

const formName = 'hasInsurances';

const QuestionForm =
  (({ handleSubmit, submitting }) => (
    <form onSubmit={handleSubmit}>
      <Lead>今年生命保険料を支払いましたか？</Lead>
      <CustomDescription field="life_insurance" as={Text} />
      <div className="u-ta-c">
        <ToggleButton options={options} name="hasLifeInsurance" />
      </div>

      <Line />

      <Lead>今年地震保険料を支払いましたか？</Lead>
      <CustomDescription field="earthquake_insurance" as={Text} />
      <div className="u-ta-c">
        <ToggleButton options={options} name="hasEarthquakeInsurance" />
      </div>

      <Line />

      <Lead>今年国民年金・国民健康保険料を支払いましたか？</Lead>
      <CustomDescription field="social_insurance" as={Text} />
      <div className="u-ta-c">
        <ToggleButton options={options} name="hasSocialInsurance" />
      </div>

      <Line />

      <Lead>今年共済契約掛金・個人型企業型年金掛金を支払いましたか？</Lead>
      <CustomDescription field="premium" as={Text} />
      <div className="u-ta-c">
        <ToggleButton options={options} name="hasPremium" />
      </div>

      <Line />

      <Lead>住宅ローン控除の申告書がありますか？</Lead>
      <CustomDescription field="housing_loan" as={Text} />
      <div className="u-ta-c">
        <ToggleButton options={options} name="hasHousingLoan" />
      </div>

      <Buttons>
        <Button primary onClick={handleSubmit} disabled={submitting}>
          次ヘ
        </Button>
      </Buttons>
    </form>
  )) |> reduxForm({ form: formName });

const initialChecked = (questionMap, question) => questionMap[question]?.status !== 'skipped';

const Question = ({ questionMap }) => {
  const { data, loading } = useQuery(INSURANCE_STATUSES);
  const [answer] = useMutation(ANSWER_HAS_INSURANCES);
  const { history } = useReactRouter();
  const initialValues = useMemo(() => {
    if (loading) return {};
    const {
      request: { profile = {} }
    } = data;
    return questionMap.has_insurances?.status === 'answered'
      ? {
          hasLifeInsurance: questionMap['life_insurances'].status !== 'skipped',
          hasEarthquakeInsurance: questionMap['earthquake_insurances'].status !== 'skipped',
          hasSocialInsurance: questionMap['social_insurances'].status !== 'skipped',
          hasPremium: questionMap['premium'].status !== 'skipped',
          hasHousingLoan: questionMap['housing_loan'].status !== 'skipped'
        }
      : {
          hasLifeInsurance: ['generalLifeInsurances', 'nursingMedicalInsurances', 'individualAnnuityInsurances'].some(
            insuranceType => !_.isEmpty(profile[insuranceType])
          ),
          hasEarthquakeInsurance: !_.isEmpty(profile.earthquakeInsurances),
          hasSocialInsurance: !_.isEmpty(profile.socialInsurances),
          hasPremium: !_.isEmpty(profile.premium),
          hasHousingLoan: !_.isEmpty(profile.housingLoan)
        };
  }, [data, loading, questionMap]);
  if (loading) return <LoadingPage />;
  return (
    <Panel>
      <Headding className={styles.primaryPanelHeader}>保険・ローンの有無</Headding>
      <Item>
        <QuestionForm
          initialValues={initialValues}
          onSubmit={async values => {
            await answer({
              variables: {
                input: values
              }
            });
            history.push('/employee_input');
          }}
        />
      </Item>
    </Panel>
  );
};

export default Question;
